import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import API from '../API';
import { FileStatusDetail, Message } from './types';
import type { Attachment, AttachmentStatus, FileUploadView } from '../chat/FileUploadView';
import {
  FILE_TOO_LARGE_MESSAGE,
  UPLOAD_FAILED_MESSAGE,
  firstOversizedFile,
  uploadErrorMessage,
} from '@dooglie/ui';

export type { Attachment, AttachmentStatus } from '../chat/FileUploadView';

export { UPLOAD_FAILED_MESSAGE };

type PresignedUploadInfo = {
  file_id?: string;
  url?: string;
  fields?: Record<string, string>;
};

/**
 * Owns file-attachment state for the discovery conversation: presigned S3
 * uploads and the background polling that updates each user message's
 * `file_upload_status`. Polling mutates the shared `messages` array via
 * `setMessages`, so the hook takes both.
 *
 * Each picked file is an `Attachment` with its own state, and only the ones
 * that actually reached S3 count as attached (`fileIds`) — a failed upload used
 * to be console-logged while the composer still said "1 file attached" and let
 * it be sent. Now it shows as a failed chip with `uploadError` beside it.
 */
export function useFileUpload(
  messages: Message[],
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>
): FileUploadView & { pollFileStatuses(messages: Message[]): Promise<void> } {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  // Only files that reached S3 are attached; a failed one is shown, not sent.
  const fileIds = useMemo(
    () =>
      attachments
        .filter((a) => a.status === 'uploaded' && a.fileId !== null)
        .map((a) => a.fileId as string),
    [attachments]
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Poll the backend for the upload status of the given (user) messages and
  // fold the results back into `messages`.
  const pollFileStatuses = useCallback(
    async (messagesToPoll: Message[]) => {
      const validMessages = messagesToPoll.filter((msg) => msg.type === 'user');

      if (validMessages.length === 0) {
        return;
      }

      const discoveryEntryIds = validMessages.map(
        (msg) => msg.discovery_entry_id
      );

      console.log(
        'Polling file_data_source for discovery_entry refs:',
        discoveryEntryIds
      );

      try {
        const res = await API.post('/file_data_source_poll', {
          refs: discoveryEntryIds.map((object_id) => ({
            object_type: 'discovery_entry' as const,
            object_id,
          })),
        });

        console.log('Poll response:', res.data);

        const statusMap = res.data || {};
        const filesMap: Record<string, FileStatusDetail> =
          statusMap.files || {};

        setMessages((currentMessages) =>
          currentMessages.map((prevMsg) => {
            if (
              prevMsg.type === 'user' &&
              prevMsg.discovery_entry_id &&
              statusMap[prevMsg.discovery_entry_id]
            ) {
              return {
                ...prevMsg,
                file_upload_status: statusMap[prevMsg.discovery_entry_id],
                file_statuses:
                  filesMap[prevMsg.discovery_entry_id]?.statuses ??
                  prevMsg.file_statuses,
              };
            }
            return prevMsg;
          })
        );
      } catch (error) {
        console.error('Error polling file status:', error);
      }
    },
    [setMessages]
  );

  // Poll IN_PROGRESS uploads every 5s; stop once none remain.
  useEffect(() => {
    const pollFileStatus = async () => {
      const currentMessagesToPoll = messages.filter(
        (msg) => msg.type === 'user' && msg.file_upload_status === 'IN_PROGRESS'
      );

      if (currentMessagesToPoll.length === 0) {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        return;
      }

      pollFileStatuses(currentMessagesToPoll);
    };

    console.log('Starting polling');
    pollingIntervalRef.current = setInterval(pollFileStatus, 5000);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [messages, pollFileStatuses]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const mostRecentBotMessage = messages.find((msg) => msg.type === 'ai');
    const discoveryEntryId = mostRecentBotMessage?.discovery_entry_id;
    if (!discoveryEntryId) {
      console.error('No discovery entry to attach files to yet');
      return;
    }

    const fileList = Array.from(files);
    // The backend refuses the whole batch when any file is over the limit;
    // say so now rather than after a presign round trip.
    if (firstOversizedFile(fileList)) {
      setAttachments(
        fileList.map((file) => ({
          name: file.name,
          fileId: null,
          status: 'failed' as const,
        }))
      );
      setUploadError(FILE_TOO_LARGE_MESSAGE);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    const setStatus = (index: number, status: AttachmentStatus) =>
      setAttachments((current) =>
        current.map((a, i) => (i === index ? { ...a, status } : a))
      );

    setUploadingFiles(true);
    setUploadError(null);
    setAttachments(
      fileList.map((file) => ({
        name: file.name,
        fileId: null,
        status: 'uploading',
      }))
    );

    try {
      const filesMetadata = fileList.map((file) => ({
        name: file.name,
        type: file.type || 'application/octet-stream',
        size: file.size,
      }));

      // Request presigned POST data from backend
      // Backend expects: { object_type, object_id, files: [{ name, type }, ...] }
      // Backend returns: [{ url, fields, file_id }, ...]
      const presignedResponse = await API.post('/file_data_source_upload', {
        object_type: 'discovery_entry',
        object_id: discoveryEntryId,
        files: filesMetadata,
      });

      const uploadData: PresignedUploadInfo[] = Array.isArray(
        presignedResponse.data
      )
        ? presignedResponse.data
        : [];

      setAttachments((current) =>
        current.map((a, i) => ({ ...a, fileId: uploadData[i]?.file_id ?? null }))
      );

      // Upload each file directly to S3 using presigned POST. Settled one by
      // one so a single failure marks that file, not the whole batch.
      const results = await Promise.allSettled(
        fileList.map(async (file, index) => {
          const uploadInfo = uploadData[index];
          if (!uploadInfo?.file_id || !uploadInfo.url || !uploadInfo.fields) {
            throw new Error(`No presigned POST data for file ${file.name}`);
          }

          const formData = new FormData();
          // Add all fields from the presigned POST response (key, policy, signature, Content-Type, etc.)
          Object.entries(uploadInfo.fields).forEach(([key, value]) => {
            formData.append(key, value);
          });
          // The file itself must be appended last
          formData.append('file', file);

          // Don't set Content-Type header - browser sets it with the boundary
          const uploadResponse = await fetch(uploadInfo.url, {
            method: 'POST',
            body: formData,
          });

          // Read the body either way. S3 answers a presigned POST with an
          // empty 204, and leaving it unread makes Chromium cancel the body
          // stream once the Response is dropped — the same request then shows
          // up as a second, aborted POST in DevTools and the e2e network log.
          // There was only ever one request; this just lets it finish.
          const responseText = await uploadResponse.text();
          if (!uploadResponse.ok) {
            throw new Error(
              `Failed to upload ${file.name} to S3: ${uploadResponse.status} ${uploadResponse.statusText} - ${responseText}`
            );
          }
        })
      );

      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          console.error('Error uploading file:', result.reason);
        }
        setStatus(index, result.status === 'fulfilled' ? 'uploaded' : 'failed');
      });

      if (results.some((r) => r.status === 'rejected')) {
        setUploadError(UPLOAD_FAILED_MESSAGE);
      }
      if (!results.some((r) => r.status === 'fulfilled')) return;

      // Kick off processing for whatever did reach S3.
      await API.post('/file_data_source_process', {
        object_type: 'discovery_entry',
        object_id: discoveryEntryId,
      });
    } catch (error) {
      // Presign or process refused: nothing in this batch is usable.
      console.error('Error uploading files:', error);
      setAttachments((current) =>
        current.map((a) => ({ ...a, status: 'failed' }))
      );
      setUploadError(uploadErrorMessage(error));
    } finally {
      setUploadingFiles(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };

  const clearFiles = () => {
    setAttachments([]);
    setUploadError(null);
  };

  return {
    attachments,
    fileIds,
    uploadError,
    uploadingFiles,
    fileInputRef,
    pollFileStatuses,
    handleFileSelect,
    handleFileButtonClick,
    clearFiles,
  };
}
